import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import logo from "../images/TP-_logo-orizz-claim-color.png";
import Table from "react-bootstrap/Table";
import { getDate, getWeek } from "date-fns";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";

const Dettagli = ({ showModal, handleCloseModal }) => {
  const [data, setData] = useState([]);
  const currentWeek = getWeek(new Date()); // Ottieni la settimana corrente
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [totalWeek, setTotalWeek] = useState(0);
  const [weekChartData, setWeekChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showWeekModal, setShowWeekModal] = useState(false);
  const [weeklyData, setWeeklyData] = useState([]);
  const [precChartData, setPrecChartData] = useState([]);
  const [webNowebChartData, setWebNowebChartData] = useState([]);
  const [monthlyTotal, setMonthlyTotal] = useState(0);

  // **DICHIARAZIONE UNICA DI TODAY, CURRENT YEAR/MONTH/DAY**
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  const currentWeekNumber = getWeek(today);
  const todayWeekDay = today.getDay(); // 0=Dom, 1=Lun, ...

  // Associazione numeri a nomi dei mesi
  const monthNames = {
    1: "Gennaio",
    2: "Febbraio",
    3: "Marzo",
    4: "Aprile",
    5: "Maggio",
    6: "Giugno",
    7: "Luglio",
    8: "Agosto",
    9: "Settembre",
    10: "Ottobre",
    11: "Novembre",
    12: "Dicembre",
  };

  useEffect(() => {
    const fetchNoPrecodiceData = async () => {
      try {
        const res = await axios.get("./mancata_vendita_dashboard.json");
        if (!res.data.TP || !res.data.TP.lastminuti) return;

        const allData = res.data.TP.lastminuti;

        // Filtra ordini senza precodice
        const noPrecodiceData = allData.filter(
          (order) =>
            !order.precodice &&
            parseInt(order.numero_settimana) === currentWeekNumber &&
            parseInt(order.numero_giorno) > 0 &&
            parseInt(order.numero_giorno) <= todayWeekDay,
        );

        // Totale settimanale (solo ordini senza precodice)

        // Totali
        const totalWeek = noPrecodiceData.reduce(
          (acc, order) => acc + parseFloat(order.ordinato),
          0,
        );

        const totalWeek_S = noPrecodiceData
          .filter((order) => order.id_ordine_fornitore == null)
          .reduce((acc, order) => acc + parseFloat(order.ordinato), 0);

        const totalWeek_C = noPrecodiceData
          .filter((order) => order.id_ordine_fornitore != null)
          .reduce((acc, order) => acc + parseFloat(order.ordinato), 0);

        // Raggruppa per giorno (grafico settimana)
        const weekTotals = {};
        noPrecodiceData.forEach((order) => {
          const day = parseInt(order.numero_giorno);
          if (!weekTotals[day]) weekTotals[day] = 0;
          weekTotals[day] += parseFloat(order.ordinato);
        });

        const weekChart = Object.keys(weekTotals).map((day) => ({
          day: dayNumberToName(day),
          total: weekTotals[day],
        }));

        // Raggruppa per origine
        const totalsByOrigine = { web: 0, noweb: 0 };
        noPrecodiceData.forEach((order) => {
          if (order.origine === "web")
            totalsByOrigine.web += parseFloat(order.ordinato);
          else totalsByOrigine.noweb += parseFloat(order.ordinato);
        });

        const webNoWebData = [
          { name: "Web", total: totalsByOrigine.web },
          { name: "NoWeb", total: totalsByOrigine.noweb },
        ];

        // Aggiorna stati
        setData(noPrecodiceData);
        setTotalWeek(totalWeek);
        setWeekChartData(weekChart);
        setWebNowebChartData(webNoWebData);
        setData(noPrecodiceData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchNoPrecodiceData();
  }, [currentWeek]);

  useEffect(() => {
    const fetchPrecodiceData = async () => {
      try {
        const res = await axios.get("./mancata_vendita_dashboard.json");
        if (!res.data.TP || !res.data.TP.lastminuti) return;

        const allData = res.data.TP.lastminuti;

        // Filtra ordini con precodice
        const precodiceData = allData.filter(
          (order) =>
            order.precodice &&
            order.precodice.trim() !== "" &&
            parseInt(order.numero_settimana) === currentWeek,
        );

        const precodiceAggregation = {};
        precodiceData.forEach((order) => {
          const code = order.precodice.trim();
          const val = parseFloat(order.ordinato) || 0;

          if (!precodiceAggregation[code]) {
            precodiceAggregation[code] = { total: 0, stock: 0, cross: 0 };
          }

          precodiceAggregation[code].total += val;

          if (order.id_ordine_for == null) {
            precodiceAggregation[code].stock += val;
          } else {
            precodiceAggregation[code].cross += val;
          }
        });

        const chartData = Object.keys(precodiceAggregation).map((code) => ({
          precodice: precodiceNames[code] || code,
          total: precodiceAggregation[code].total,
          stock: precodiceAggregation[code].stock,
          cross: precodiceAggregation[code].cross,
        }));

        setPrecChartData(chartData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchPrecodiceData();
  }, [currentWeek]);

  const totalsStockCross = React.useMemo(() => {
    if (!Array.isArray(data)) return { stock: 0, crossdocking: 0 };

    const today = new Date();
    const currentWeekNumber = getWeek(today);

    const filteredData = data.filter(
      (order) =>
        (!order.precodice || order.precodice.trim() === "") &&
        parseInt(order.numero_settimana) === currentWeekNumber,
    );

    const stock = filteredData
      .filter((order) => !order.id_ordine_for)
      .reduce((acc, order) => acc + parseFloat(order.ordinato || 0), 0);

    const crossdocking = filteredData
      .filter((order) => order.id_ordine_for)
      .reduce((acc, order) => acc + parseFloat(order.ordinato || 0), 0);

    return { stock, crossdocking };
  }, [data]);

  const totalWeekStockCross =
    totalsStockCross.stock + totalsStockCross.crossdocking;

  console.log("Totale settimana (no precodice):", totalWeekStockCross);

  // Nuova funzione per calcolare i totali mese per mese solo per ordini senza precodice
  const calcolaTotalePerMeseNoPrecodice = (data) => {
    const monthlyTotals = {};

    if (Array.isArray(data)) {
      data.forEach((order) => {
        // Consideriamo solo ordini con precodice null o stringa vuota
        if (!order.precodice || order.precodice.trim() === "") {
          const monthNumber = parseInt(order.mese_ordine);
          const ordinato = parseFloat(order.ordinato);

          if (!isNaN(monthNumber) && !isNaN(ordinato)) {
            if (!monthlyTotals[monthNumber]) monthlyTotals[monthNumber] = 0;
            monthlyTotals[monthNumber] += ordinato;
          }
        }
      });
    }

    return monthlyTotals;
  };

  const calcolaTotaliPerMeseDueAnni = (data) => {
    const result = {};

    if (Array.isArray(data)) {
      data.forEach((order) => {
        // Solo senza precodice
        if (!order.precodice || order.precodice.trim() === "") {
          const month = parseInt(order.mese_ordine);
          const year = parseInt(order.numero_anno);

          const ordinatoCorrente = parseFloat(order.ordinato || 0);
          const ordinatoPrecedente = parseFloat(
            order["ordinato meno un anno"] || 0,
          );

          const orderDate = new Date(order.data_ordine);

          if (isNaN(month) || isNaN(year) || isNaN(orderDate)) return;

          // ❌ Escludi mesi futuri
          if (month > currentMonth) return;

          // ❌ Taglia i giorni futuri del mese corrente
          if (month === currentMonth && orderDate.getDate() > currentDay)
            return;

          if (!result[month]) {
            result[month] = { current: 0, previous: 0 };
          }

          if (year === currentYear) {
            // Anno corrente → usa "ordinato"
            result[month].current += ordinatoCorrente;
          } else if (year === currentYear - 1) {
            // Anno scorso → usa "ordinato meno un anno"
            result[month].previous += ordinatoPrecedente;
          }
        }
      });
    }

    return result;
  };

  const monthlyTotalsTwoYears = calcolaTotaliPerMeseDueAnni(data);

  // Oggetto di mapping precodice => nome
  const precodiceNames = {
    0: "ART. DI COMODO",
    "001": "ASSO",
    "003": "NIPPARTS",
    "004": "MALO'",
    "007": "BMW",
    "008": "ALFA ROMEO (DISUSO)",
    "009": "FCA GROUP",
    "010": "LANCIA (DISUSO)",
    "011": "BEHR TERMOT TRONIK",
    "012": "CITROEN",
    "013": "MERCEDES",
    "014": "OPEL (DISUSO)",
    "015": "PSA - OPEL",
    "016": "CHAMPION",
    "017": "NISSAN",
    "018": "BOSCH",
    "019": "VALEO",
    "020": "VW GROUP",
    "023": "TRASH",
    "026": "FORD",
    "027": "MANN",
    "029": "FISPA",
    "030": "BERU",
    "031": "IVECO",
    "032": "RHIBO",
    "034": "VICTORY",
    "035": "LUK",
    "037": "UFI",
    "039": "MONROE AMMORTIZZATORI",
    "041": "SKODA",
    "043": "PORSCHE",
    "045": "DAYCO EUROPE SRL",
    "048": "TRW",
    "050": "RENAULT",
    "051": "VOLVO",
    "052": "HONDA",
    "053": "KIA",
    "054": "MASERATI",
    "055": "MG",
    "056": "FORD INTERNATIONAL",
    "060": "PEUGEOT NEW",
    "062": "CHRYSLER",
    "063": "JEEP - CHRYSLER",
    "069": "LAMBORGHINI",
    "071": "TDS",
    "075": "HYUNDAI",
    "080": "TOYOTA",
    "089": "MAGNETI MARELLI",
    "090": "MITSUBISHI",
    "091": "SSANGYONG",
    "097": "BMW MOTO",
    "099": "PURFLUX",
    1: "SPESE DI TRASPORTO (DISUSO)",
    100: "GENERAL MOTORS",
    110: "AUDI",
    111: "SEAT",
    112: "VIC",
    113: "VOLKSWAGEN",
    126: "RICAMBI VARI",
    140: "SUZUKI",
    142: "LAND ROVER",
    143: "JAGUAR",
    150: "RENAULT TRUCKS",
    184: "STARK",
    190: "GABRIEL",
    194: "EX FCA ESCLUSIVI JEEP",
    195: "FORD AMERICA",
    200: "BYD",
    276: "YUASA",
    488: "FERRARI",
    Z1: "SPESE DI TRASPORTO",
    ZG: "Gabbie ed Imballi vuoti da Rendere",
    ZM: "Imballi Vuoti Magazzino",
    ZZ: "Fatturazione libera",
  };

  const calcolaTotaleSettimanaCorrente = (orders) => {
    return orders.reduce((acc, order) => {
      const ordinato = parseFloat(order.ordinato);
      return acc + (isNaN(ordinato) ? 0 : ordinato); // Se NaN, somma 0 invece
    }, 0);
  };

  const dayNumberToName = (dayNumber) => {
    switch (parseInt(dayNumber)) {
      case 1:
        return "Lunedì";
      case 2:
        return "Martedì";
      case 3:
        return "Mercoledì";
      case 4:
        return "Giovedì";
      case 5:
        return "Venerdì";
      case 6:
        return "Sabato";
      default:
        return "Domenica";
    }
  };

  const weeklyTotals = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
  };

  // Raggruppa i dati della settimana corrente
  if (Array.isArray(data)) {
    data.forEach((order) => {
      const dayNumber = parseInt(order.numero_giorno);
      if (weeklyTotals[dayNumber] !== undefined) {
        weeklyTotals[dayNumber] += parseFloat(order.ordinato);
      }
    });
  }

  // Assicuriamoci che `data` contenga solo gli ordini della settimana corrente
  data.forEach((order) => {
    const dayNumber = parseInt(order.numero_giorno);
    if (weeklyTotals[dayNumber] !== undefined) {
      weeklyTotals[dayNumber] += parseFloat(order.ordinato);
    }
  });

  // Filtra solo gli ordini della settimana corrente
  // Filtra solo gli ordini della settimana corrente
  const currentWeekData = Array.isArray(data)
    ? data.filter((order) => {
        const date = new Date(order.data_ordine);
        return !isNaN(date) && getWeek(date) === currentWeek;
      })
    : [];

  // Raggruppa per cliente E giorno (stesso cliente + stesso giorno)
  const groupedByClientAndDay = currentWeekData.reduce((acc, order) => {
    const client = order.ragione_sociale?.trim() || "Sconosciuto";
    const date = new Date(order.data_ordine);
    const dayName = dayNumberToName(order.numero_giorno);
    const dateKey = date.toLocaleDateString("it-IT"); // esempio: 28/10/2025
    const key = `${client}_${dateKey}`; // chiave unica cliente+giorno

    if (!acc[key]) {
      acc[key] = {
        ragioneSociale: client,
        dayName,
        dataAcquisto: dateKey,
        totaleOrdinato: 0,
      };
    }

    acc[key].totaleOrdinato += parseFloat(order.ordinato || 0);
    return acc;
  }, {});

  // Converti in array per la tabella
  const tableData = Object.values(groupedByClientAndDay).map((item) => ({
    ragioneSociale: item.ragioneSociale,
    dayName: item.dayName,
    dataAcquisto: item.dataAcquisto,
    ordinato: item.totaleOrdinato.toFixed(2),
  }));

  // (Opzionale) ordina per data e poi per importo decrescente
  const sortedTableData = tableData.sort((a, b) => {
    const dateA = new Date(a.dataAcquisto.split("/").reverse().join("-"));
    const dateB = new Date(b.dataAcquisto.split("/").reverse().join("-"));
    if (dateA - dateB !== 0) return dateB - dateA; // ordina per data
    return parseFloat(b.ordinato) - parseFloat(a.ordinato); // poi per totale
  });

  const filteredData = data.filter(
    (order) => getWeek(new Date(order.data_ordine)) === currentWeek,
  );

  const totals = filteredData.reduce(
    (acc, order) => {
      if (order.origine === "web") {
        acc.web += parseFloat(order.ordinato);
      } else {
        acc.noweb += parseFloat(order.ordinato);
      }
      return acc;
    },
    { web: 0, noweb: 0 },
  );

  const formatToEuro = (amount) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Creiamo un oggetto per i totali mensili
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("./mancata_vendita_dashboard.json");
        console.log("Dati ricevuti:", res.data);

        if (!res.data.TP || !res.data.TP.lastminuti) {
          console.error("Formato JSON errato!");
          return;
        }

        setData(res.data.TP.lastminuti);
      } catch (error) {
        console.error("Errore nel recupero dei dati:", error);
      }
    };

    fetchData();
  }, []);

  // Funzione per calcolare il totale ordinato per ogni mese
  const calcolaTotalePerMese = (data) => {
    const monthlyTotals = {};

    if (Array.isArray(data)) {
      data.forEach((order) => {
        const monthNumber = parseInt(order.mese_ordine);
        const ordinato = parseFloat(order.ordinato);

        if (!isNaN(monthNumber) && !isNaN(ordinato)) {
          if (!monthlyTotals[monthNumber]) {
            monthlyTotals[monthNumber] = 0;
          }
          monthlyTotals[monthNumber] += ordinato;
        }
      });
    }

    return monthlyTotals;
  };

  const monthlyTotals = calcolaTotalePerMese(data);

  console.log("Dati mensili:", monthlyTotals);

  // Dati per il grafico
  const monthChartData = Object.keys(monthNames).map((key) => ({
    month: monthNames[key],
    total: monthlyTotals[key] || 0, // Mostra 0 se non ci sono dati
  }));

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleMonthClick = (monthNumber) => {
    setSelectedMonth(monthNumber);

    // Filtra ordini solo senza precodice per il mese selezionato e anno corrente
    const monthFilteredData = data.filter(
      (order) =>
        (!order.precodice || order.precodice.trim() === "") &&
        parseInt(order.mese_ordine) === monthNumber &&
        parseInt(order.numero_anno) === currentYear,
    );

    // Raggruppa per settimana
    const weeklyAggregation = {};
    let totalMonth = 0;

    monthFilteredData.forEach((order) => {
      const week = parseInt(order.numero_settimana);
      const ord = parseFloat(order.ordinato) || 0;

      weeklyAggregation[week] = (weeklyAggregation[week] || 0) + ord;
      totalMonth += ord;
    });

    // Converte in array ordinato per settimana
    const weeklyList = Object.keys(weeklyAggregation)
      .sort((a, b) => a - b)
      .map((week) => ({
        week,
        total: weeklyAggregation[week],
      }));

    setWeeklyData(weeklyList);
    setMonthlyTotal(totalMonth);
    setShowWeekModal(true); // mostra il modale
  };

  // Filtra i dati per il mese selezionato
  const monthFilteredData = data.filter(
    (order) => parseInt(order.mese_ordine) === selectedMonth,
  );

  // Raggruppa per settimana
  const weeklyAggregation = {};
  monthFilteredData.forEach((order) => {
    const weekNumber = getWeek(new Date(order.data_ordine));
    weeklyAggregation[weekNumber] =
      (weeklyAggregation[weekNumber] || 0) + parseFloat(order.ordinato);
  });

  // Converti in array ordinato
  const weekList = Object.keys(weeklyAggregation)
    .sort((a, b) => a - b)
    .map((week) => ({
      week: `Settimana ${week}`,
      total: weeklyAggregation[week].toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    }));

  const monthChartDataNoPrecodice = Object.keys(monthNames)
    .map((key) => parseInt(key)) // mesi da 1 a 12
    .map((month) => ({
      mese: month,
      month: monthNames[month],
      current: monthlyTotalsTwoYears[month]?.current || 0, // se non ci sono dati → 0
      previous: monthlyTotalsTwoYears[month]?.previous || 0, // se non ci sono dati → 0
    }));

  //---------------------------------------------------------------------------------------------GRAFICO 1

  // Dati della settimana corrente per anno corrente
  const weeklyCurrentYearData = data.filter(
    (order) =>
      parseInt(order.numero_anno) === currentYear &&
      parseInt(order.numero_settimana) === currentWeek &&
      (!order.precodice || order.precodice.trim() === ""),
  );

  // Raggruppa per origine
  const weeklyWebNoWebData = weeklyCurrentYearData.reduce(
    (acc, order) => {
      const val = parseFloat(order.ordinato) || 0;
      if (order.origine === "web") acc.web += val;
      else acc.noweb += val;
      return acc;
    },
    { web: 0, noweb: 0 },
  );

  // Prepara array per il BarChart verticale
  const weeklyWebNoWebChartData = [
    { name: "Web", total: weeklyWebNoWebData.web },
    { name: "NoWeb", total: weeklyWebNoWebData.noweb },
  ];

  //---------------------------------------------------------------------------------------------GRAFICO 2

  // Dati settimana corrente e anno corrente, senza precodice
  const weekCurrentYearData = data.filter(
    (order) =>
      parseInt(order.numero_anno) === currentYear &&
      parseInt(order.numero_settimana) === currentWeek &&
      (!order.precodice || order.precodice.trim() === ""),
  );

  // Raggruppa per giorno della settimana
  const weekChartCurrentYearData = weekCurrentYearData.reduce((acc, order) => {
    const dayNumber = parseInt(order.numero_giorno); // 1 = lunedì ... 7 = domenica
    const ordinato = parseFloat(order.ordinato) || 0;
    if (!acc[dayNumber]) acc[dayNumber] = 0;
    acc[dayNumber] += ordinato;
    return acc;
  }, {});

  // Convertilo in array ordinato e con nome giorno
  const weekChartDataByDay = Object.keys(weekChartCurrentYearData)
    .sort((a, b) => a - b)
    .map((dayNumber) => ({
      day: dayNumberToName(dayNumber), // funzione che hai già definito
      total: weekChartCurrentYearData[dayNumber],
    }));

  //---------------------------------------------------------------------------------------------GRAFICO 3

  // Filtra solo ordini senza precodice della settimana corrente e anno 2026
  const currentWeek2026Data = data.filter(
    (order) =>
      parseInt(order.numero_anno) === 2026 &&
      parseInt(order.numero_settimana) === currentWeek &&
      (!order.precodice || order.precodice.trim() === ""),
  );

  // Raggruppa per cliente e giorno (stesso cliente + stesso giorno)
  const groupedByClientAndDay2026 = currentWeek2026Data.reduce((acc, order) => {
    const client = order.ragione_sociale?.trim() || "Sconosciuto";
    const date = new Date(order.data_ordine);
    const dayName = dayNumberToName(order.numero_giorno);
    const dateKey = date.toLocaleDateString("it-IT"); // es: 11/02/2026
    const key = `${client}_${dateKey}`; // chiave unica cliente+giorno

    if (!acc[key]) {
      acc[key] = {
        ragioneSociale: client,
        dayName,
        dataAcquisto: dateKey,
        totaleOrdinato: 0,
      };
    }

    acc[key].totaleOrdinato += parseFloat(order.ordinato || 0);
    return acc;
  }, {});

  // Converti in array per la tabella
  const sortedTableData2026 = Object.values(groupedByClientAndDay2026).sort(
    (a, b) => {
      const dateA = new Date(a.dataAcquisto.split("/").reverse().join("-"));
      const dateB = new Date(b.dataAcquisto.split("/").reverse().join("-"));
      if (dateA - dateB !== 0) return dateB - dateA; // ordina per data
      return parseFloat(b.totaleOrdinato) - parseFloat(a.totaleOrdinato); // poi per totale
    },
  );

  return (
    <>
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        className="rounded-0 modale-test w-100 p-5"
      >
        {" "}
        <Modal.Header className="bg-white flex-column">
          {" "}
          {/* Titolo centrato */}{" "}
          <Modal.Title className="text-modal bg-white mx-auto text-center fw-bold">
            {" "}
            Dettagli Last Minute - Week {currentWeek}{" "}
          </Modal.Title>
          {/* Spazio tra titolo e info */}
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex w-100 justify-content-between mt-3 p-3">
            {/* Totale ordinato a sinistra */}
            <span
              style={{ fontWeight: "bold", fontSize: "1.2rem", color: "#000" }}
            >
              Totale ordinato: {formatToEuro(totalWeekStockCross)}
            </span>

            {/* Stock e Crossdocking a destra */}
            <span
              className="text-secondary"
              style={{ fontSize: "1rem", fontWeight: "bold" }}
            >
              Stock: {formatToEuro(totalsStockCross.stock)} &nbsp;|&nbsp;
              Crossdocking: {formatToEuro(totalsStockCross.crossdocking)}
            </span>
          </div>
          {Array.isArray(data) && data.length === 0 ? (
            <div className="text-center fs-4">
              Dati non disponibili per questa settimana
            </div>
          ) : (
            <div className="d-flex flex-wrap bg-white">
              <Card className="flex-card card-modale bg-light text-dark">
                <ResponsiveContainer width="100%" maxHeight="300px">
                  <BarChart
                    layout="vertical"
                    width={500}
                    height={300}
                    data={weeklyWebNoWebChartData} // <<<<<<<<<< solo settimana corrente e anno corrente
                    margin={{
                      top: 20,
                      right: 30,
                      left: 50,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value) =>
                        `${parseFloat(value).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      }
                    />
                    <YAxis dataKey="name" type="category" />
                    <Tooltip
                      formatter={(value, name) => [
                        `${parseFloat(value).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        name,
                      ]}
                    />
                    <Bar dataKey="total" fill="#b81f25" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card className="flex-card card-modale bg-light text-dark">
                <ResponsiveContainer width="100%" maxHeight="300px">
                  <BarChart
                    width={500}
                    height={300}
                    data={weekChartDataByDay} // <<<<<<<<<< solo anno corrente
                    margin={{ top: 10, right: 15, left: 50, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis
                      tickFormatter={(value) =>
                        `${parseFloat(value).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      }
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        `${parseFloat(value).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        name,
                      ]}
                    />
                    <Bar dataKey="total" fill="gray" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card className="flex-card card-modale bg-light text-dark">
                <ResponsiveContainer
                  width="100%"
                  maxHeight="300px"
                  padding={50}
                >
                  <Card.Body
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      padding: 0,
                    }}
                  >
                    <Table bordered hover>
                      <thead
                        style={{
                          backgroundColor: "white",
                          color: "white",
                        }}
                      >
                        <tr>
                          <th
                            className="sticky-th"
                            style={{
                              backgroundColor: "#b81f25",
                              color: "white",
                            }}
                          >
                            Brand
                          </th>
                          <th
                            className="sticky-th"
                            style={{
                              backgroundColor: "#b81f25",
                              color: "white",
                            }}
                          >
                            Totale
                          </th>
                          <th
                            className="sticky-th"
                            style={{
                              backgroundColor: "#b81f25",
                              color: "white",
                            }}
                          >
                            Stock
                          </th>
                          <th
                            className="sticky-th"
                            style={{
                              backgroundColor: "#b81f25",
                              color: "white",
                            }}
                          >
                            Cross dock.
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {precChartData
                          .sort((a, b) => b.total - a.total)
                          .map((item, index) => (
                            <tr key={index}>
                              <td className="fw-bold bg-transparent">
                                {item.precodice}
                              </td>
                              <td className="bg-transparent text-end">
                                {formatToEuro(item.total)}
                              </td>
                              <td className="bg-transparent text-end">
                                {formatToEuro(item.stock)}
                              </td>
                              <td className="bg-transparent text-end">
                                {formatToEuro(item.cross)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </ResponsiveContainer>
              </Card>

              <Card className="flex-card card-modale bg-light text-dark d-flex p-3">
                <Card.Body
                  style={{ maxHeight: "250px", overflowY: "auto", padding: 0 }}
                >
                  <Table>
                    <thead>
                      <tr>
                        <th
                          className="sticky-th"
                          style={{ backgroundColor: "#b81f25", color: "white" }}
                        >
                          {" "}
                          Ragione Sociale{" "}
                        </th>{" "}
                        <th
                          className="sticky-th"
                          style={{
                            backgroundColor: "#b81f25",
                            color: "white",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSort("dayName")}
                        >
                          {" "}
                          Giorno{" "}
                        </th>{" "}
                        <th
                          className="sticky-th"
                          style={{ backgroundColor: "#b81f25", color: "white" }}
                        >
                          {" "}
                          Data{" "}
                          {sortColumn === "dayName"
                            ? sortDirection === "asc"
                              ? "↑"
                              : "↓"
                            : ""}{" "}
                        </th>{" "}
                        <th
                          className="sticky-th"
                          style={{
                            backgroundColor: "#b81f25",
                            color: "white",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSort("ordinato")}
                        >
                          {" "}
                          Ordinato{" "}
                          {sortColumn === "ordinato"
                            ? sortDirection === "asc"
                              ? "↑"
                              : "↓"
                            : ""}{" "}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedTableData2026.map((order, index) => (
                        <tr key={index}>
                          <td>{order.ragioneSociale}</td>
                          <td>{order.dayName}</td>
                          <td>{order.dataAcquisto}</td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatToEuro(order.totaleOrdinato)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card
                className="flex-card card-modale bg-light text-dark d-flex scrollable-list"
                style={{ maxHeight: "300px" }}
              >
                <Card.Body>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthChartDataNoPrecodice} // usa TUTTI i mesi, non filtrare
                      margin={{ top: 0, right: 10, left: 70, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis
                        tickFormatter={(value) =>
                          `${parseFloat(value).toLocaleString("it-IT", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        }
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${parseFloat(value).toLocaleString("it-IT", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`,
                          name,
                        ]}
                      />

                      {/* Barre anno corrente */}
                      <Bar
                        dataKey="current"
                        name="Anno corrente"
                        fill="#b81f25"
                        label={({ x, y, width, value, index }) => {
                          const previous =
                            monthChartDataNoPrecodice[index]?.previous || 0;
                          const current = value || 0;

                          if (!current && !previous) return null;

                          const trendPercent = previous
                            ? ((current - previous) / previous) * 100
                            : current > 0
                              ? 100
                              : 0;

                          if (trendPercent === 0) return null;

                          const isPositive = trendPercent > 0;

                          const arrow = isPositive ? "▲" : "▼";

                          const offsetX = x + width / 2;
                          const offsetY = y - 5;

                          return (
                            <text
                              x={offsetX}
                              y={offsetY}
                              textAnchor="middle"
                              fill={isPositive ? "green" : "red"}
                              fontSize={8}
                              fontWeight="bold"
                            >
                              {arrow} {trendPercent.toFixed(1)}%
                            </text>
                          );
                        }}
                        onClick={(data, index) => {
                          const monthNumber =
                            monthChartDataNoPrecodice[index].mese;
                          handleMonthClick(monthNumber); // apre il modale con il mese cliccato
                        }}
                      />

                      {/* Barre anno scorso */}
                      <Bar
                        dataKey="previous"
                        name="Anno scorso"
                        fill="#777777"
                        label={({ x, y, width, value }) => {
                          if (!value) return null;
                          return (
                            <text
                              x={x + width / 2}
                              y={y - 8}
                              textAnchor="middle"
                              fill="#555"
                              fontSize={8}
                              fontWeight="bold"
                            >
                              2025
                            </text>
                          );
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <img
            src={logo}
            alt=""
            className="mx-auto p-2"
            style={{ width: "200px" }}
          />
        </Modal.Footer>
      </Modal>

      <Modal
        show={showWeekModal}
        className="modale-dettaglio-root"
        dialogClassName="modale-dettaglio-dialog"
        onHide={() => setShowWeekModal(false)}
        centered
        size="sm"
        backdrop={true}
        contentClassName="p-2"
      >
        <Modal.Title className="text-modal fw-bold bg-white mx-auto text-center">
          {selectedMonth ? monthNames[selectedMonth] : "Dettaglio mese"}
        </Modal.Title>

        <div style={{ width: "260px", margin: "0 auto", padding: "10px" }}>
          {weeklyData.length === 0 ? (
            <div className="text-center">Nessun dato disponibile</div>
          ) : (
            <ul className="list-unstyled mb-2">
              {weeklyData.map((item) => (
                <li
                  key={item.week}
                  className="mb-1 d-flex justify-content-between"
                >
                  <strong>SETTIMANA {item.week}:</strong>
                  <span>
                    {item.total.toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="d-flex justify-content-between fw-bold mt-2 border-top pt-2">
            <span>TOTALE:</span>
            <span>
              {monthlyTotal.toLocaleString("it-IT", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
              })}
            </span>
          </div>
        </div>

        <Modal.Footer>
          <img
            src={logo}
            alt=""
            className="mx-auto p-2"
            style={{ width: "200px" }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Dettagli;
